import 'rxjs/add/observable/from';
import 'rxjs/add/observable/empty';
import 'rxjs/add/operator/mergeMap';
import { Observable } from 'rxjs/Observable';
import type { Location, RouterActionReplace } from '@atlassian/jira-router';
import { isViewSettingAsPanelExpEnabledWithNoExposure } from '../../feature-flags';
import { INSIGHTS_PANEL, VIEW_SETTINGS_PANEL } from '../../model/panels/constants';
import { QUERY_PARAM_IS_INSIGHTS_OPEN } from '../../model/routing/constants';
import { unsetPanelState } from '../../services/local-storage';
import { isInsightsPanelOpen } from '../../services/panel';
import { setPanelsUrl } from '../../services/routing/panels-transformer';
import {
	type TogglePanelAction,
	type CloseInsightsPanelAction,
	CLOSE_INSIGHTS_PANEL,
	TOGGLE_PANEL,
	closeViewSettingsPanel,
	closeInsightsPanel,
} from '../../state/actions/panels';
import { getRapidViewId } from '../../state/selectors/app';
import { getViewSettingsPanel } from '../../state/selectors/panels';
import type { MiddlewareAPI, ActionsObservable, Action } from '../../state/types';

export const onCloseInsightsEpic =
	(getLocation: () => Location, replace: RouterActionReplace) =>
	(action$: ActionsObservable, store: MiddlewareAPI) =>
		action$.ofType(CLOSE_INSIGHTS_PANEL).mergeMap((action: CloseInsightsPanelAction) => {
			const state = store.getState();
			if (action.payload && action.payload.shouldUpdateLocation) {
				// We only set the URL if this action is called with `shouldUpdateLocation`
				// Mainly because there is a case user will click on an issue and it auto close the insights panel
				// In this action we will fire two actions.
				// 1. Update the URL selected issue,
				// 2. Update the URL for insights panel.
				// However, these two actions fire at one cycle, and we can't get the updated route location if we fire both actions
				// at the same time. Therefore, the second push action (here) which update the URL will override the first push action,
				// it cause the URL is not changed after we select an issue.
				// Therefore, we will fire all the update to the URL in one push on when the issue card is clicked.
				replace(setPanelsUrl(QUERY_PARAM_IS_INSIGHTS_OPEN, false, getLocation()));
			}

			// Make sure Insights is removed from the local storage
			unsetPanelState(INSIGHTS_PANEL, getRapidViewId(state));

			return Observable.empty<Action>();
		});

// Update URL when panels are toggled
export const onTogglePanelEpic =
	(getLocation: () => Location, replace: RouterActionReplace) =>
	(action$: ActionsObservable, store: MiddlewareAPI) =>
		action$.ofType(TOGGLE_PANEL).mergeMap((action: TogglePanelAction) => {
			const { panel } = action.payload;
			const state = store.getState();

			if (panel === INSIGHTS_PANEL) {
				const isInsightsOpen = isInsightsPanelOpen();
				const isViewSettingsPanelOpen = getViewSettingsPanel(state);
				replace(setPanelsUrl(QUERY_PARAM_IS_INSIGHTS_OPEN, !isInsightsOpen, getLocation()));

				if (isViewSettingAsPanelExpEnabledWithNoExposure() && isViewSettingsPanelOpen) {
					return Observable.of(closeViewSettingsPanel());
				}
			}

			if (isViewSettingAsPanelExpEnabledWithNoExposure() && panel === VIEW_SETTINGS_PANEL) {
				return Observable.of(closeInsightsPanel(true));
			}

			return Observable.empty<Action>();
		});
